import { envValue, type EnvRecord } from "./envPreflight";

/**
 * U1 RESEARCH. The three switches of the research agent, each exactly "1" on and
 * unset, empty or "0" off; anything else is refused rather than read as on
 * (the `consultIntent.ts` rule: an intent this code cannot read is never
 * guessed on, CLAUDE.md section 0). The parsers live here so the API (which
 * decides whether a `lead.research` task is queued) and the worker (which
 * decides whether it writes) refuse exactly the same values.
 *
 *  - ACKRA_ENABLE_RESEARCH: the task writes cards. Off, a queued task completes
 *    with a `lead.research.ran` row (outcome skipped) and reads nothing.
 *  - ACKRA_RESEARCH_MODEL: the one cheap model step (tone and interest labels).
 *    Changes behaviour, so it lands only after a real eval run on Kabir's typed go.
 *  - ACKRA_RESEARCH_LOOKUP: the `profile.lookup` tool. Off, no lookup is made.
 *
 * All three default off.
 */

export const ENABLE_RESEARCH_ENV = "ACKRA_ENABLE_RESEARCH";
export const RESEARCH_MODEL_ENV = "ACKRA_RESEARCH_MODEL";
export const RESEARCH_LOOKUP_ENV = "ACKRA_RESEARCH_LOOKUP";

/** Null when the value is a valid flag, or the problem sentence. Never quotes the value. */
export function validateResearchFlag(raw: string): string | null {
  if (raw === "0" || raw === "1") return null;
  return 'must be exactly "1" (on) or "0" (off). Any other value is refused rather than read as on';
}

/** True when the flag, read as set, is exactly "1". */
export function researchFlagRequested(env: EnvRecord, name: string): boolean {
  return envValue(env, name) !== undefined && env[name] === "1";
}

export function planResearchFlag(env: EnvRecord, name: string): boolean {
  if (envValue(env, name) === undefined) return false;
  const complaint = validateResearchFlag(env[name] ?? "");
  if (complaint !== null) throw new Error(`${name} ${complaint}.`);
  return env[name] === "1";
}

export interface ResearchPlan {
  /** The task writes cards. */
  readonly enabled: boolean;
  /** The tone and interest model step runs. Only meaningful with `enabled`. */
  readonly model: boolean;
  /** `profile.lookup` may be called. Only meaningful with `enabled`. */
  readonly lookup: boolean;
}

export function planResearch(env: EnvRecord): ResearchPlan {
  return {
    enabled: planResearchFlag(env, ENABLE_RESEARCH_ENV),
    model: planResearchFlag(env, RESEARCH_MODEL_ENV),
    lookup: planResearchFlag(env, RESEARCH_LOOKUP_ENV),
  };
}
